import { useState } from 'react';
import { createTrip, listTrips } from '../lifeos/api.js';
import { daysBetween, daysLeftLabel, tripDates, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { text } from '../lifeos/forms.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, EmptyState, Group, IconButton, IconTile, Loader, Page, Row, Fold, Meta, useParamSetter, useSphereLens, When } from '../ui/index.js';
import type { Trip } from '../lifeos/types.js';

/** Trips (`/trips`): upcoming first, by start date; past trips fold away. */
export default function Trips(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const lens = useSphereLens(spheres, sphere, (k) => setParam('sphere', k));
  const list = useLoad(() => listTrips({ sphere: sphere ?? undefined }), [sphere]);
  const [adding, setAdding] = useState(false);
  const today = ymdInHomeZone(new Date());

  return (
    <Page
      title="Trips"
      actions={
        <>
          {lens.button}
          <IconButton icon="plus" label="Add" onClick={() => setAdding(true)} />
        </>
      }
    >
      {lens.chip && <div className="ui-chips">{lens.chip}</div>}
      <Loader state={list}>
        {(items) => {
          if (items.length === 0)
            return (
              <EmptyState
                icon="plane"
                title="No trips yet."
                hint="Add one to keep flights, stays and packing together."
                action={
                  <Button variant="secondary" icon="plus" onClick={() => setAdding(true)}>
                    Add a trip
                  </Button>
                }
              />
            );
          const ended = (t: Trip): boolean => (t.endsOn ?? t.startsOn ?? '9999') < today;
          const upcoming = items.filter((t) => !ended(t)).sort((a, b) => (a.startsOn ?? '9999').localeCompare(b.startsOn ?? '9999'));
          const past = items.filter(ended).sort((a, b) => (b.startsOn ?? '').localeCompare(a.startsOn ?? ''));
          const row = (t: Trip): JSX.Element => {
            const n = t.startsOn ? daysBetween(today, t.startsOn) : null;
            const packed = t.packing.filter((p) => p.packed).length;
            const live = !ended(t);
            return (
              <Row
                key={t.id}
                to={`/trips/${t.id}`}
                tone={live ? 'default' : 'muted'}
                leading={<IconTile name="plane" color={live ? '#0a7c72' : '#8e8e93'} />}
                title={t.title}
                sub={
                  <Meta color={sphereColor(spheres, t.sphereKey)}>
                    {[t.destination, tripDates(t, today)].filter(Boolean).join(' · ')}
                    {live && t.packing.length > 0 && ` · ${packed}/${t.packing.length} packed`}
                  </Meta>
                }
                detail={
                  n !== null && n >= 0 ? (
                    <When tone={n <= 7 ? 'today' : 'later'}>{daysLeftLabel(n)}</When>
                  ) : n !== null && live ? (
                    <When tone="today">now</When>
                  ) : undefined
                }
              />
            );
          };
          return (
            <>
              <Group label="Upcoming trips">{upcoming.map(row)}</Group>
              <Group header="Past" label="Past trips">
                {past.length > 0 && <Fold items={past} n={0} noun={past.length === 1 ? 'past trip' : 'past trips'} render={row} />}
              </Group>
            </>
          );
        }}
      </Loader>
      {lens.sheet}
      {adding && (
        <FormSheet
          title="Add a trip"
          submitLabel="Add"
          fields={[
            { key: 'title', label: 'Title', required: true, wide: true },
            { key: 'destination', label: 'Where', wide: true },
            { key: 'startsOn', label: 'From', kind: 'date' },
            { key: 'endsOn', label: 'Until', kind: 'date' },
            { key: 'sphereKey', label: 'Sphere', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
          ]}
          initial={{ sphereKey: 'personal' }}
          onClose={() => setAdding(false)}
          onSubmit={async (v) => {
            await createTrip({
              title: text(v.title)!,
              destination: text(v.destination),
              startsOn: text(v.startsOn),
              endsOn: text(v.endsOn),
              sphereKey: v.sphereKey || undefined,
            });
            void list.reload();
          }}
        />
      )}
    </Page>
  );
}
